import type { CandidateStatus } from '@eduvy/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/icons';
import { Badge, Button, Chip, Empty, ErrorNote, Input, PageHeader, SkeletonCard, cx } from '../components/ui';
import { api, type JobSummary } from '../lib/api';

const OUTREACH: Record<JobSummary['outreachMode'], { label: string; icon: IconName }> = {
  WEB_LINK: { label: 'Web link', icon: 'globe' },
  PHONE_CALL: { label: 'Phone call', icon: 'phone' },
  PHONE_THEN_WEB: { label: 'Phone, then web', icon: 'phone' },
};

const STATUS_TONE = { ACTIVE: 'green', DRAFT: 'slate', CLOSED: 'red' } as const;

/** The pipeline bar on a job card: one segment per group of statuses. */
const SEGMENTS: Array<{ label: string; statuses: CandidateStatus[]; colour: string }> = [
  { label: 'New', statuses: ['NEW'], colour: 'var(--faint)' },
  { label: 'In progress', statuses: ['QUEUED', 'INVITED', 'CALLING', 'IN_INTERVIEW', 'INTERVIEWED'], colour: '#a8279a' },
  { label: 'Qualified', statuses: ['QUALIFIED', 'SHORTLISTED', 'ASSESSMENT_INVITED', 'IN_ASSESSMENT', 'ASSESSED', 'MEETING_INVITED', 'SELECTED'], colour: 'var(--success)' },
  { label: 'Review', statuses: ['NEEDS_REVIEW', 'ON_HOLD'], colour: 'var(--warning)' },
  { label: 'Out', statuses: ['NOT_QUALIFIED', 'UNREACHABLE', 'REJECTED', 'WITHDRAWN'], colour: 'var(--destructive)' },
];

type StatusFilter = 'all' | JobSummary['status'];

export function JobsPage() {
  const jobs = useQuery({ queryKey: ['jobs'], queryFn: () => api.get<JobSummary[]>('/api/jobs') });
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState<StatusFilter>('all');
  const q = (params.get('q') ?? '').trim().toLowerCase();
  const shown = jobs.data?.filter((j) => (status === 'all' || j.status === status) && (!q || `${j.title} ${j.location} ${j.department ?? ''}`.toLowerCase().includes(q)));
  const counts = { all: jobs.data?.length ?? 0, ACTIVE: 0, DRAFT: 0, CLOSED: 0 };
  for (const j of jobs.data ?? []) counts[j.status]++;

  return (
    <>
      <PageHeader
        eyebrow="Jobs"
        title="Open roles"
        subtitle="Each job has its own interview plan, qualification rules and lead list. Open one to import leads and start interviews."
        actions={
          <Button variant="primary" icon="plus" to="/jobs/new">
            New job
          </Button>
        }
      />

      {jobs.data && jobs.data.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2">
          {(['all', 'ACTIVE', 'DRAFT', 'CLOSED'] as const).map((s) => (
            <Chip key={s} size="sm" on={status === s} onClick={() => setStatus(s)}>
              {s === 'all' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
              <span className="font-mono text-[10px] opacity-70">{counts[s]}</span>
            </Chip>
          ))}
          <div className="ml-auto w-full sm:w-64">
            <Input
              type="search"
              value={params.get('q') ?? ''}
              onChange={(e) => setParams(e.target.value ? { q: e.target.value } : {})}
              placeholder="Search jobs"
              aria-label="Search jobs"
              leading={<Icon name="search" className="h-4 w-4" />}
            />
          </div>
        </div>
      )}

      <ErrorNote error={jobs.error} />
      {jobs.isLoading && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <SkeletonCard key={i} lines={4} />
          ))}
        </div>
      )}
      {jobs.data?.length === 0 && (
        <Empty
          icon="briefcase"
          title="No jobs yet"
          action={
            <Button variant="primary" icon="sparkles" to="/jobs/new">
              Create the first job
            </Button>
          }
        >
          Pick a role from the library (or describe your own), and the AI drafts the interview and the skills assessment for your requirements. Then import your leads, and the AI interviews every one.
        </Empty>
      )}
      {jobs.data && jobs.data.length > 0 && shown?.length === 0 && (
        <Empty icon="search" title="No matching jobs">
          Try another search or filter.
        </Empty>
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {shown?.map((job, i) => (
          <JobCard key={job.id} job={job} index={i} />
        ))}
      </div>
    </>
  );
}

function JobCard({ job, index }: { job: JobSummary; index: number }) {
  const s = job.counts.byStatus;
  const total = job.counts.total;
  const sum = (statuses: CandidateStatus[]) => statuses.reduce((n, st) => n + (s[st] ?? 0), 0);
  const segments = SEGMENTS.map((seg) => ({ ...seg, n: sum(seg.statuses) }));
  const outreach = OUTREACH[job.outreachMode];
  const headline = [
    ['Leads', total],
    ['In progress', segments[1]!.n],
    ['Qualified', segments[2]!.n],
    ['Review', segments[3]!.n],
  ] as const;
  return (
    <Link
      to={`/jobs/${job.id}`}
      className={cx('surface surface-hover group relative flex flex-col overflow-hidden rounded-lg p-5 fade-up', index < 4 && `fade-up-${index + 1}`)}
      style={{ animationDelay: index >= 4 ? `${Math.min(index, 12) * 40}ms` : undefined }}
    >
      <span className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-brand-gradient opacity-0 blur-2xl transition duration-300 group-hover:opacity-[0.12]" aria-hidden />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="display line-clamp-2 text-[20px] font-medium leading-tight text-fg transition group-hover:text-accent">{job.title}</h2>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-fg">
            <span className="inline-flex items-center gap-1">
              <Icon name="pin" className="h-3.5 w-3.5 text-faint" />
              {job.location}
            </span>
            <span>{job.workMode}</span>
            <span className="inline-flex items-center gap-1">
              <Icon name={outreach.icon} className="h-3.5 w-3.5 text-faint" />
              {outreach.label}
            </span>
          </p>
        </div>
        <Badge tone={STATUS_TONE[job.status]} dot>
          {job.status.charAt(0) + job.status.slice(1).toLowerCase()}
        </Badge>
      </div>

      <div className="relative mt-5">
        <div className="flex h-2.5 w-full gap-px overflow-hidden rounded-full bg-muted" role="img" aria-label={segments.map((x) => `${x.label} ${x.n}`).join(', ')}>
          {total > 0 &&
            segments.filter((x) => x.n > 0).map((x) => <span key={x.label} className="h-full first:rounded-l-full last:rounded-r-full" style={{ width: `${(x.n / total) * 100}%`, background: x.colour }} />)}
        </div>
        <dl className="mt-4 grid grid-cols-4 gap-2">
          {headline.map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dd className="display text-[22px] font-medium leading-none tabular text-fg">{value}</dd>
              <dt className="mt-1 truncate font-mono text-[10px] uppercase tracking-wider text-muted-fg">{label}</dt>
            </div>
          ))}
        </dl>
      </div>

      <div className="relative mt-5 flex items-center justify-between border-t border-border pt-3.5 text-xs text-muted-fg">
        <span>Created {new Date(job.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
        <span className="inline-flex items-center gap-1 font-semibold text-accent opacity-0 transition group-hover:opacity-100">
          Open pipeline <Icon name="arrowRight" className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
}
